import { useMemo, useState } from 'react';
import { useLookups, useRows } from '@/hooks/useLive';
import { useView } from '@/hooks/useView';
import { date as fmtDate, humanise, num, relativeDays } from '@/lib/format';
import { useAuthStore } from '@/stores/authStore';
import { roleLock } from '@/lib/editability';
import { Panel } from '@/components/ui/Panel';
import { Button } from '@/components/ui/Button';
import { StatTile } from '@/components/ui/StatTile';
import { StatusChip } from '@/components/ui/StatusChip';
import { ragTone } from '@/components/ui/statusTone';
import { DataTable, type Column } from '@/components/ui/DataTable';
import { ExportBar } from '@/components/ui/ExportBar';
import { figures, tableRows } from '@/lib/export';
import { PartDrawer } from './parts/PartDrawer';
import { MovementDrawer } from './parts/MovementDrawer';
import { PurchaseOrderDrawer } from './parts/PurchaseOrderDrawer';

export interface PartStock {
  part_id: string;
  part_no: string;
  description: string;
  category: string | null;
  is_critical: boolean;
  min_level: number;
  max_level: number | null;
  bin_location: string | null;
  unit_cost: number | null;
  supplier_name: string | null;
  qty_on_hand: number;
  qty_on_order: number;
  expected_date: string | null;
  status_colour: string;
  stock_value: number;
}

interface BlockingPart {
  breakdown_id: string;
  breakdown_no: string;
  fleet_no: string;
  failure_description: string;
  started_at: string;
  part_no: string;
  part_description: string | null;
  qty_required: number;
  qty_on_hand: number;
  qty_on_order: number;
  eta_date: string | null;
}

interface Movement extends Record<string, unknown> {
  id: string;
  part_id: string;
  movement_date: string;
  movement_type: string;
  qty_delta: number;
  unit_cost: number | null;
  machine_id: string | null;
  reference: string | null;
  notes: string | null;
}

interface PurchaseOrder extends Record<string, unknown> {
  id: string;
  po_no: string;
  supplier_id: string | null;
  order_date: string | null;
  expected_date: string | null;
  status: string;
  currency: string;
}

interface PoLine extends Record<string, unknown> {
  id: string;
  po_id: string;
  part_id: string;
  qty_ordered: number;
  qty_received: number;
  unit_cost: number | null;
}

const PO_TONE: Record<string, 'grey' | 'blue' | 'amber' | 'green' | 'red'> = {
  draft: 'grey',
  requested: 'blue',
  approved: 'blue',
  ordered: 'amber',
  partially_received: 'amber',
  received: 'green',
  cancelled: 'grey',
};

const MOVEMENT_TONE: Record<string, 'grey' | 'blue' | 'amber' | 'green' | 'red'> = {
  receipt: 'green',
  opening: 'blue',
  return: 'green',
  issue: 'amber',
  scrap: 'red',
  adjustment: 'grey',
};

export function Parts() {
  const [tab, setTab] = useState<'stock' | 'movements' | 'orders'>('stock');
  const [part, setPart] = useState<Record<string, unknown> | null>(null);
  const [movement, setMovement] = useState<Record<string, unknown> | null>(null);
  const [order, setOrder] = useState<Record<string, unknown> | null>(null);

  const movements = useRows<Movement>('part_stock_movements', 'movement_date');
  const orders = useRows<PurchaseOrder>('purchase_orders', 'order_date');
  const lines = useRows<PoLine>('purchase_order_lines');
  const partRows = useRows<Record<string, unknown>>('parts', 'part_no');
  const lookups = useLookups([
    { table: 'parts', label: 'part_no' },
    { table: 'machines', label: 'fleet_no' },
    { table: 'suppliers', label: 'name' },
  ]);

  const { rows: stock } = useView<PartStock>('v_parts_stock');
  const { rows: blocking } = useView<BlockingPart>('v_parts_blocking_repairs');

  const canEdit = useAuthStore((s) => s.hasRole('maintenance', 'manager', 'admin'));
  const role = useAuthStore((s) => s.profile?.role);
  // Everyone may open a record; who may change it is said inside (0046).
  const lock = roleLock(role, ['maintenance', 'manager', 'admin'], 'parts, stock and orders');

  const summary = useMemo(() => {
    const list = stock ?? [];
    return {
      below: list.filter((p) => p.status_colour !== 'green').length,
      criticalOut: list.filter((p) => p.is_critical && p.qty_on_hand <= 0).length,
      value: list.reduce((s, p) => s + (Number(p.stock_value) || 0), 0),
      onOrder: list.filter((p) => p.qty_on_order > 0).length,
      openOrders: (orders ?? []).filter(
        (o) => !['received', 'cancelled', 'draft'].includes(o.status),
      ).length,
      blocking: (blocking ?? []).length,
    };
  }, [stock, orders, blocking]);

  const lineCount = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines ?? []) m.set(l.po_id, (m.get(l.po_id) ?? 0) + 1);
    return m;
  }, [lines]);

  const orderValue = useMemo(() => {
    const m = new Map<string, number>();
    for (const l of lines ?? []) {
      m.set(l.po_id, (m.get(l.po_id) ?? 0) + Number(l.qty_ordered) * Number(l.unit_cost ?? 0));
    }
    return m;
  }, [lines]);

  const stockColumns: Column<PartStock>[] = [
    {
      key: 'status_colour', label: '', width: '92px',
      // Sorted by severity, not by the colour's name - the shortages that stop
      // a machine belong at the top of the list.
      value: (p) => ({ red: 0, amber: 1, green: 2 }[p.status_colour] ?? 3),
      render: (p) => (
        <StatusChip tone={ragTone(p.status_colour)}>
          {p.qty_on_hand <= 0 ? 'Out' : p.status_colour === 'amber' ? 'Low' : 'OK'}
        </StatusChip>
      ),
    },
    {
      key: 'part_no', label: 'Part',
      render: (p) => (
        <>
          <strong>{p.part_no}</strong>
          {p.is_critical && <span className="small" style={{ color: 'var(--rag-red)' }}> · critical</span>}
        </>
      ),
    },
    { key: 'description', label: 'Description' },
    { key: 'category', label: 'Category' },
    {
      key: 'qty_on_hand', label: 'On hand', align: 'right',
      render: (p) => <span className="tabular">{num(p.qty_on_hand, 0)}</span>,
    },
    {
      key: 'min_level', label: 'Minimum', align: 'right',
      render: (p) => <span className="tabular">{num(p.min_level, 0)}</span>,
    },
    {
      key: 'qty_on_order', label: 'On order', align: 'right',
      render: (p) => (p.qty_on_order > 0
        ? <span className="tabular">{num(p.qty_on_order, 0)}</span>
        : <span className="muted">—</span>),
    },
    {
      key: 'expected_date', label: 'Expected',
      render: (p) => (p.expected_date ? relativeDays(p.expected_date) : '—'),
    },
    { key: 'bin_location', label: 'Bin' },
    {
      key: 'stock_value', label: 'Value', align: 'right',
      render: (p) => <span className="tabular">{num(p.stock_value, 2)}</span>,
    },
  ];

  const movementColumns: Column<Movement>[] = [
    { key: 'movement_date', label: 'Date', render: (m) => fmtDate(m.movement_date) },
    {
      key: 'part_id', label: 'Part',
      value: (m) => lookups.parts?.get(m.part_id) ?? '',
      render: (m) => <strong>{lookups.parts?.get(m.part_id) ?? '—'}</strong>,
    },
    {
      key: 'movement_type', label: 'Movement',
      render: (m) => (
        <StatusChip tone={MOVEMENT_TONE[m.movement_type] ?? 'grey'}>
          {humanise(m.movement_type)}
        </StatusChip>
      ),
    },
    {
      key: 'qty_delta', label: 'Quantity', align: 'right',
      render: (m) => (
        <span className="tabular" style={{ color: m.qty_delta < 0 ? 'var(--rag-amber)' : undefined }}>
          {m.qty_delta > 0 ? '+' : ''}{num(m.qty_delta, 0)}
        </span>
      ),
    },
    {
      key: 'machine_id', label: 'Machine',
      value: (m) => lookups.machines?.get(m.machine_id ?? '') ?? '',
      render: (m) => lookups.machines?.get(m.machine_id ?? '') ?? '—',
    },
    { key: 'reference', label: 'Reference' },
    { key: 'notes', label: 'Notes' },
  ];

  const orderColumns: Column<PurchaseOrder>[] = [
    { key: 'po_no', label: 'PO', render: (o) => <strong>{o.po_no}</strong> },
    {
      key: 'supplier_id', label: 'Supplier',
      value: (o) => lookups.suppliers?.get(o.supplier_id ?? '') ?? '',
      render: (o) => lookups.suppliers?.get(o.supplier_id ?? '') ?? '—',
    },
    { key: 'order_date', label: 'Ordered', render: (o) => fmtDate(o.order_date) },
    {
      key: 'expected_date', label: 'Expected',
      render: (o) => (o.expected_date ? relativeDays(o.expected_date) : '—'),
    },
    {
      key: 'lines', label: 'Lines', align: 'right',
      value: (o) => lineCount.get(o.id) ?? 0,
      render: (o) => <span className="tabular">{lineCount.get(o.id) ?? 0}</span>,
    },
    {
      key: 'value', label: 'Value', align: 'right',
      value: (o) => orderValue.get(o.id) ?? 0,
      render: (o) => <span className="tabular">{num(orderValue.get(o.id) ?? 0, 2)}</span>,
    },
    {
      key: 'status', label: 'Status',
      render: (o) => <StatusChip tone={PO_TONE[o.status] ?? 'grey'}>{humanise(o.status)}</StatusChip>,
    },
  ];

  return (
    <div className="col" style={{ gap: 'var(--sp-3)' }}>
      <div className="row">
        <ExportBar title="Parts & stock"
          subtitle={tab === 'stock' ? 'Stock' : tab === 'movements' ? 'Stock ledger' : 'Purchase orders'}
          file={`parts-${tab}`} landscape disabled={!stock}
          sheets={() => [
            { name: 'Summary', rows: figures([
              ['Below minimum', summary.below], ['Critical out of stock', summary.criticalOut],
              ['Delaying repairs', summary.blocking], ['Lines on order', summary.onOrder],
              ['Open orders', summary.openOrders], ['Stock value', Math.round(summary.value * 100) / 100, 'N$']]) },
            { name: 'Stock', rows: (stock ?? []).map((p) => ({
              Status: p.status_colour === 'red' ? 'short' : p.status_colour === 'amber' ? 'below minimum' : 'ok',
              ...tableRows(stockColumns, [p])[0],
              'On hand': Number(p.qty_on_hand), 'On order': Number(p.qty_on_order), 'Value N$': Number(p.stock_value) || 0 })) },
            { name: 'Delaying repairs', rows: (blocking ?? []).map((b) => ({
              Machine: b.fleet_no, Part: b.part_no ?? '', Description: b.part_description ?? '',
              Required: Number(b.qty_required), 'On hand': Number(b.qty_on_hand), 'On order': Number(b.qty_on_order),
              ETA: b.eta_date ?? '' })) },
            { name: 'Movements', rows: tableRows(movementColumns, movements) },
            { name: 'Purchase orders', rows: tableRows(orderColumns, orders) },
          ]} />
      </div>
      {(blocking ?? []).length > 0 && (
        <Panel title={`Parts delaying repairs · ${(blocking ?? []).length}`}>
          <div className="col">
            {(blocking ?? []).map((b, i) => (
              <div className="issue issue--block" key={`${b.breakdown_id}|${b.part_no}|${i}`}
                style={{ alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ minWidth: 90 }}>{b.fleet_no}</strong>
                <span>{b.part_description ?? b.part_no}</span>
                <span className="small">
                  need {num(b.qty_required, 0)} · have {num(b.qty_on_hand, 0)}
                  {b.qty_on_order > 0 ? ` · ${num(b.qty_on_order, 0)} on order` : ''}
                </span>
                <span className="spacer" />
                <span className="small">
                  {b.eta_date ? `ETA ${relativeDays(b.eta_date)}` : 'no ETA — not yet ordered'}
                </span>
              </div>
            ))}
          </div>
          <p className="small muted" style={{ marginBottom: 0 }}>
            These are the shortages costing production right now — a machine is down
            and the part it needs is not on the shelf.
          </p>
        </Panel>
      )}

      <Panel title="Stock position">
        <div className="grid grid--stats">
          <StatTile label="Below minimum" value={summary.below}
            rag={summary.below ? 'amber' : 'green'} />
          <StatTile label="Critical out of stock" value={summary.criticalOut}
            rag={summary.criticalOut ? 'red' : 'green'} />
          <StatTile label="Delaying repairs" value={summary.blocking}
            rag={summary.blocking ? 'red' : 'green'} />
          <StatTile label="Lines on order" value={summary.onOrder} />
          <StatTile label="Open orders" value={summary.openOrders} />
          <StatTile label="Stock value" value={summary.value} decimals={2} />
        </div>
      </Panel>

      <div className="row" style={{ gap: 'var(--sp-2)' }}>
        <Button variant={tab === 'stock' ? 'primary' : 'secondary'} onClick={() => setTab('stock')}>
          Stock
        </Button>
        <Button variant={tab === 'movements' ? 'primary' : 'secondary'} onClick={() => setTab('movements')}>
          Movements
        </Button>
        <Button variant={tab === 'orders' ? 'primary' : 'secondary'} onClick={() => setTab('orders')}>
          Purchase orders
        </Button>
        <span className="spacer" />
        {canEdit && (
          <>
            {tab === 'stock' && <Button onClick={() => setPart({})}>+ New part</Button>}
            {tab === 'movements' && (
              <>
                <Button variant="secondary" onClick={() => setMovement({ movement_type: 'issue' })}>
                  Issue
                </Button>
                <Button onClick={() => setMovement({ movement_type: 'receipt' })}>Receive</Button>
              </>
            )}
            {tab === 'orders' && <Button onClick={() => setOrder({})}>+ New order</Button>}
          </>
        )}
      </div>

      {tab === 'stock' && (
        <Panel title="Parts catalogue" flush>
          <DataTable
            rows={stock}
            columns={stockColumns}
            rowKey={(p) => p.part_id}
            onRowClick={(p) => setPart((partRows ?? []).find((r) => String(r.id) === p.part_id) ?? { id: p.part_id })}
            defaultSort="status_colour"
            searchPlaceholder="Search part number, description, bin…"
            empty="No parts on the catalogue yet."
          />
        </Panel>
      )}

      {tab === 'movements' && (
        <Panel title="Stock ledger" flush>
          <div style={{ padding: 'var(--sp-3) var(--sp-3) 0' }} className="small muted">
            Quantity on hand is the sum of this ledger — there is no editable stock
            figure anywhere in the system, so what the shelf holds can always be
            explained by the movements that put it there.
          </div>
          <DataTable
            rows={movements}
            columns={movementColumns}
            rowKey={(m) => m.id}
            onRowClick={(m) => setMovement(m)}
            defaultSort="movement_date"
            searchPlaceholder="Search part, reference, machine…"
            empty="No stock movements recorded."
          />
        </Panel>
      )}

      {tab === 'orders' && (
        <Panel title="Purchase orders" flush>
          <DataTable
            rows={orders}
            columns={orderColumns}
            rowKey={(o) => o.id}
            onRowClick={(o) => setOrder(o)}
            defaultSort="order_date"
            searchPlaceholder="Search order number, supplier…"
            empty="No purchase orders raised."
          />
        </Panel>
      )}

      {part && <PartDrawer record={part} stock={stock ?? []} locked={lock} onClose={() => setPart(null)} />}
      {movement && <MovementDrawer record={movement} stock={stock ?? []} locked={lock} onClose={() => setMovement(null)} />}
      {order && <PurchaseOrderDrawer record={order} locked={lock} onClose={() => setOrder(null)} />}
    </div>
  );
}
